const ROLE_ALIASES = Object.freeze({
  'department-admin': 'department_admin',
  team_admin: 'department_admin',
  'team-admin': 'department_admin',
  'tournament-admin': 'tournament_admin',
  individualAdmin: 'individual_admin',
  individualadmin: 'individual_admin',
  'individual-admin': 'individual_admin',
  tournamentCreator: 'tournament_creator',
  tournamentcreator: 'tournament_creator',
  'tournament-creator': 'tournament_creator',
});

export const normalizeRole = (role) => {
  if (!role) return null;
  if (typeof role === 'string') {
    const normalized = role.trim().toLowerCase().replace(/[-\s]+/g, '_');
    return ROLE_ALIASES[role] || ROLE_ALIASES[normalized] || normalized;
  }
  if (typeof role === 'object') {
    const resolved = role.type || role.role || role.name || null;
    return normalizeRole(resolved);
  }
  return null;
};

const normalizeScopeType = (scopeType) => {
  if (!scopeType) return null;
  const normalized = String(scopeType).trim().toLowerCase().replace(/[-\s]+/g, '_');
  if (normalized === 'org') return 'organization';
  return normalized;
};

const normalizeScopeId = (value) => (value === null || value === undefined ? '' : String(value));


const getGlobalRoles = (user) => new Set(
  [...(user?.roles || []), user?.role]
    .map((role) => normalizeRole(role))
    .filter(Boolean)
);

const readScopedAssignments = (user) => {
  const directAssignments = Array.isArray(user?.roleAssignments) ? user.roleAssignments : [];
  const nestedAssignments = Array.isArray(user?.roles?.assignments) ? user.roles.assignments : [];
  const scopedRolesObject = user?.scopedRoles && typeof user.scopedRoles === 'object'
    ? user.scopedRoles
    : {};

  const bucketScopeTypeMap = {
    organizations: 'organization',
    communities: 'community',
    clubs: 'club',
  };

  const normalizedScopedAssignments = Object.entries(scopedRolesObject)
    .flatMap(([bucket, entities]) => {
      const scopeType = normalizeScopeType(bucketScopeTypeMap[String(bucket || '').toLowerCase()] || bucket);
      if (!scopeType || !entities || typeof entities !== 'object') return [];

      return Object.entries(entities).flatMap(([scopeId, roles]) =>
        (Array.isArray(roles) ? roles : [])
          .map((role) => ({ role, scopeType, scopeId }))
      );
    });

  return [...directAssignments, ...nestedAssignments, ...normalizedScopedAssignments];
};

export const hasScopedRole = (user, role, scopeType, scopeId) => {
  const requiredRole = normalizeRole(role);
  if (!requiredRole) return false;

  const expectedScopeType = normalizeScopeType(scopeType);
  const expectedScopeId = normalizeScopeId(scopeId);
  const globalRoles = getGlobalRoles(user);
  const isScopedCheck = Boolean(expectedScopeType && expectedScopeId);

  if (globalRoles.has('super_admin')) {
    return true;
  }

  if (globalRoles.has(requiredRole) && !isScopedCheck) {
    return true;
  }

  if (globalRoles.has(requiredRole) && isScopedCheck) {
    const legacyScopeId = normalizeScopeId(
      expectedScopeType === 'community'
        ? user?.community?._id || user?.community?.id || user?.community
        : expectedScopeType === 'organization'
          ? user?.organization?._id || user?.organization?.id || user?.organization
          : expectedScopeType === 'club'
            ? user?.club?._id || user?.club?.id || user?.club
            : null
    );

    if (legacyScopeId && legacyScopeId === expectedScopeId) {
      return true;
    }
  }

  return readScopedAssignments(user).some((assignment) => {
    const assignmentRole = normalizeRole(assignment?.role || assignment?.type || assignment?.name);
    if (assignmentRole !== requiredRole) {
      return false;
    }

    const assignmentScopeType = normalizeScopeType(
      assignment?.scopeType
      || assignment?.entityType
      || assignment?.scope?.type
      || assignment?.entity?.type
    );

    const assignmentScopeId = normalizeScopeId(
      assignment?.scopeId
      || assignment?.entityId
      || assignment?.scope?.id
      || assignment?.entity?._id
      || assignment?.entity?.id
    );

    const scopeTypeMatches = !expectedScopeType || assignmentScopeType === expectedScopeType;
    const scopeIdMatches = !expectedScopeId || assignmentScopeId === expectedScopeId;
    return scopeTypeMatches && scopeIdMatches;
  });
};

export const getUserRoles = (user) => {
  const scopedRoles = readScopedAssignments(user)
    .map((assignment) => normalizeRole(assignment?.role || assignment?.type || assignment?.name))
    .filter(Boolean);

  const roles = new Set(
    [...(user?.roles || []), user?.role, ...scopedRoles]
      .map((role) => normalizeRole(role))
      .filter(Boolean)
  );

  if (user?.accountType === 'super_admin') {
    roles.add('super_admin');
  }

  if (user?.accountType === 'individual') {
    roles.add('individual');
  }

  if (roles.has('organization_admin') && user?.community && !user?.organization) {
    roles.add('community_admin');
  }

  return roles;
};


export const userHasRole = (user, role) => {
  const normalizedRole = normalizeRole(role);
  if (!normalizedRole) return false;
  return getUserRoles(user).has(normalizedRole);
};
export const isSuperAdmin = (user) => getUserRoles(user).has('super_admin');

export const hasIndividualAccess = (user) => {
  const roles = getUserRoles(user);
  return roles.has('individual') || user?.accountType === 'individual';
};
